import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  getSessions, getTerms, getClasses, getClassResults,
  getResultTemplates, exportResults,
} from '@/api/owner';
import {
  Download, FileText, Loader2, AlertCircle, Users, ArrowRight,
  CheckCircle2, FolderArchive, X,
} from 'lucide-react';
import { resolveStorageUrl } from '@/utils/storage';

const arr = <T,>(src: any): T[] => {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  if (Array.isArray(src.data)) return src.data;
  if (Array.isArray(src.data?.data)) return src.data.data;
  return [];
};
const unwrap = (r: any) => r?.data?.data ?? r?.data;
const num = (n?: any) => (n == null || n === '' ? '—' : Number(n).toFixed(1));

export function ClassResultsPage() {
  const [sessionId, setSessionId] = useState<number | ''>('');
  const [termId, setTermId] = useState<number | ''>('');
  const [classId, setClassId] = useState<number | ''>('');
  const [templateId, setTemplateId] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<number | null>(null);
  const [zipProgress, setZipProgress] = useState<{ done: number; total: number } | null>(null);

  const sessionsQ = useQuery({ queryKey: ['owner-sessions'], queryFn: () => getSessions().then(unwrap) });
  const termsQ = useQuery({ queryKey: ['owner-terms'], queryFn: () => getTerms().then(unwrap) });
  const classesQ = useQuery({ queryKey: ['owner-classes'], queryFn: () => getClasses({ per_page: 200 }).then(unwrap) });
  const templatesQ = useQuery({ queryKey: ['owner-result-templates'], queryFn: () => getResultTemplates().then(unwrap) });

  const sessions = arr<any>(sessionsQ.data);
  const terms = arr<any>(termsQ.data);
  const classes = arr<any>(classesQ.data);
  const templates = arr<any>(templatesQ.data);

  const visibleTerms = useMemo(
    () => sessionId ? terms.filter((t: any) => (t.session_id ?? t.academic_session_id) === Number(sessionId)) : terms,
    [terms, sessionId]
  );

  useEffect(() => {
    if (!sessionId && sessions.length > 0) {
      const cur = sessions.find((s: any) => s.is_current) ?? sessions[0];
      setSessionId(cur.id);
    }
  }, [sessions, sessionId]);

  useEffect(() => {
    if (!termId && visibleTerms.length > 0) {
      const cur = visibleTerms.find((t: any) => t.is_current);
      if (cur) setTermId(cur.id);
    }
    if (termId && !visibleTerms.some((t: any) => t.id === termId)) setTermId('');
  }, [visibleTerms, termId]);

  useEffect(() => {
    if (!templateId && templates.length > 0) setTemplateId(templates[0].id);
  }, [templates, templateId]);

  const enabled = !!classId && !!termId;

  const resultsQ = useQuery({
    queryKey: ['class-results', classId, termId],
    queryFn: () => getClassResults(Number(classId), { term_id: Number(termId) }).then(unwrap),
    enabled,
  });

  const data = resultsQ.data;
  const rows = arr<any>(data?.results);
  const missing = arr<any>(data?.without_result);
  const publishedRows = rows.filter((r: any) => r.status === 'published' || r.status === 'held');

  const downloadOne = async (studentId: number, rowKey: number) => {
    if (!templateId) { setError('Pick a template first.'); return; }
    setDownloading(rowKey);
    setError(null);
    try {
      const r: any = await exportResults({
        template_id: Number(templateId),
        student_id: studentId,
        term_id: Number(termId),
        type: 'student',
      });
      const d = unwrap(r);
      const path = d?.path;
      if (path) {
        // Open in new tab so it lands as a real download
        const url = resolveStorageUrl(path);
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener';
        a.click();
      } else {
        setError('Export returned no path.');
      }
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  const downloadAll = async () => {
    if (!templateId) { setError('Pick a template first.'); return; }
    if (rows.length === 0) { setError('No results to download.'); return; }
    setError(null);
    setZipProgress({ done: 0, total: rows.length });
    let failed = 0;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      try {
        const r: any = await exportResults({
          template_id: Number(templateId),
          student_id: row.student_id,
          term_id: Number(termId),
          type: 'student',
        });
        const d = unwrap(r);
        const path = d?.path;
        if (path) {
          const url = resolveStorageUrl(path);
          const a = document.createElement('a');
          a.href = url;
          a.target = '_blank';
          a.rel = 'noopener';
          a.click();
          // Small delay so browsers don't block multiple downloads
          await new Promise(res => setTimeout(res, 400));
        } else {
          failed++;
        }
      } catch {
        failed++;
      }
      setZipProgress({ done: i + 1, total: rows.length });
    }
    setZipProgress(null);
    if (failed > 0) {
      setError(`${failed} of ${rows.length} download(s) failed. The browser may have blocked some. Try again or download individually.`);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Assessments & Results</p>
          <h1 className="text-3xl font-extrabold text-slate-900">Class Results</h1>
          <p className="text-slate-500 text-sm mt-1">
            Ranked results per class · download individual sheets or the whole class
          </p>
        </div>
        {enabled && rows.length > 0 && (
          <button onClick={downloadAll} disabled={!templateId || zipProgress !== null}
            className="px-4 py-2 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-2">
            {zipProgress ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> {zipProgress.done} / {zipProgress.total}</>
            ) : (
              <><FolderArchive className="w-4 h-4" /> Download all ({rows.length})</>
            )}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Session *</label>
            <select value={sessionId} onChange={(e) => { setSessionId(e.target.value ? Number(e.target.value) : ''); setTermId(''); }}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Choose session…</option>
              {sessions.map((s: any) => <option key={s.id} value={s.id}>{s.name}{s.is_current ? ' (active)' : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Term *</label>
            <select value={termId} onChange={(e) => setTermId(e.target.value ? Number(e.target.value) : '')}
              disabled={!sessionId}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white disabled:opacity-50">
              <option value="">Choose term…</option>
              {visibleTerms.map((t: any) => <option key={t.id} value={t.id}>{t.name}{t.is_current ? ' (current)' : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Class *</label>
            <select value={classId} onChange={(e) => setClassId(e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Choose class…</option>
              {classes.map((c: any) => <option key={c.id} value={c.id}>{c.name}{c.arm ? ` · ${c.arm}` : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Template</label>
            <select value={templateId} onChange={(e) => setTemplateId(e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Choose template…</option>
              {templates.map((t: any) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-900">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {!enabled && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
          Pick a session, term, and class to see ranked results.
        </div>
      )}

      {enabled && resultsQ.isLoading && (
        <Loader2 className="w-5 h-5 animate-spin text-slate-500 mx-auto block" />
      )}

      {enabled && data && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat label="Students in class" value={data.students_in_class ?? 0} />
            <Stat label="Results computed" value={rows.length} />
            <Stat label="Published" value={publishedRows.length} tone="green" />
            <Stat label="Missing" value={missing.length} tone={missing.length > 0 ? 'amber' : 'slate'} />
          </div>

          {/* Missing warning */}
          {missing.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
              <b>{missing.length}</b> student(s) have no result yet. Compute results in Results → Publish first.
            </div>
          )}

          {/* Results table */}
          {rows.length === 0 ? (
            <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
              <Users className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="font-semibold text-slate-700">No results computed yet</p>
              <p className="text-sm text-slate-500 mt-1">
                Go to Publish → Compute results for this class + term first.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex justify-between items-center">
                <p className="font-bold text-slate-900">
                  {data.class} · {data.term}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(data.counts ?? {}).map(([status, count]: [string, any]) => (
                    <span key={status} className="px-2 py-0.5 text-xs font-bold rounded-full bg-slate-100 text-slate-700">
                      {count} {status}
                    </span>
                  ))}
                </div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-3 text-left w-10">#</th>
                    <th className="px-3 py-3 text-left min-w-[160px]">Student</th>
                    <th className="px-3 py-3 text-left w-24">Adm. No.</th>
                    <th className="px-3 py-3 text-right w-20">Total</th>
                    <th className="px-3 py-3 text-right w-20">Average</th>
                    <th className="px-3 py-3 text-right w-16">Pos</th>
                    <th className="px-3 py-3 text-center w-16">Grade</th>
                    <th className="px-3 py-3 text-left w-24">Status</th>
                    <th className="px-3 py-3 text-right w-28">Download</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r: any, idx: number) => (
                    <tr key={r.result_id} className={r.held ? 'bg-amber-50/40' : 'hover:bg-slate-50'}>
                      <td className="px-3 py-2 text-xs text-slate-500 font-mono">{idx + 1}</td>
                      <td className="px-3 py-2 font-medium text-slate-900">
                        {r.name}
                        {r.held && <span className="ml-2 text-[10px] font-bold text-amber-700">HELD</span>}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-500">{r.admission_no}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold">{num(r.total)}</td>
                      <td className="px-3 py-2 text-right font-mono">{num(r.average)}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.position ?? '—'}</td>
                      <td className="px-3 py-2 text-center font-bold">{r.grade ?? '—'}</td>
                      <td className="px-3 py-2 text-xs">
                        <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded-full border ${
                          r.status === 'published' ? 'bg-green-50 text-green-700 border-green-200' :
                          r.status === 'held'      ? 'bg-amber-50 text-amber-800 border-amber-200' :
                          r.status === 'approved'  ? 'bg-violet-50 text-violet-700 border-violet-200' :
                          r.status === 'computed'  ? 'bg-blue-50 text-blue-700 border-blue-200' :
                          'bg-slate-100 text-slate-600 border-slate-200'
                        }`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => downloadOne(r.student_id, idx)}
                          disabled={!templateId || downloading === idx || zipProgress !== null}
                          className="text-xs font-semibold text-blue-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50">
                          {downloading === idx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                          {downloading === idx ? 'PDF…' : 'PDF'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 space-y-1">
            <p><b>Download all</b> fires one PDF per student — the browser may ask you to allow multiple downloads.</p>
            <p>For a single PDF of the entire class, use <b>Broadsheet → Export class PDF</b>.</p>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: any; tone?: 'green' | 'amber' | 'slate' }) {
  const t = tone === 'green' ? 'text-green-700' : tone === 'amber' ? 'text-amber-700' : 'text-slate-900';
  return (
    <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
      <p className="text-xs font-semibold text-slate-500">{label}</p>
      <p className={`text-2xl font-extrabold mt-1 ${t}`}>{value}</p>
    </div>
  );
}
